import type {ShopOrdersPage, ShopOrderStatus} from '~/types/shopOrder'

export type ShopOrderTone = 'warning' | 'info' | 'success' | 'danger' | 'neutral'

const CANCELLED: ShopOrderStatus[] = ['cancelled_by_clinic', 'cancelled_by_owner', 'cancelled_by_admin', 'expired']

export const shopOrderPaths = {
    ownerList: () => '/profile/orders/',
    owner: (id: number) => `/profile/orders/${id}/`,
    clinicList: () => '/profile-clinic/orders/',
    clinic: (id: number) => `/profile-clinic/orders/${id}/`,
}

/** Подпись счётчика в меню: «1 активный заказ», «2 активных заказа», «5 активных заказов». */
export function activeShopOrdersCountLabel(count: number): string {
    const mod100 = Math.abs(count) % 100
    const mod10 = mod100 % 10
    const noun = mod100 > 10 && mod100 < 20
        ? 'активных заказов'
        : mod10 === 1
            ? 'активный заказ'
            : mod10 >= 2 && mod10 <= 4
                ? 'активных заказа'
                : 'активных заказов'

    return `${count} ${noun}`
}

export function isShopOrderCancelled(status: ShopOrderStatus): boolean {
    return CANCELLED.includes(status)
}

export function shopOrderTone(status: ShopOrderStatus): ShopOrderTone {
    if (status === 'sent') return 'info'
    if (status === 'confirmed' || status === 'ready' || status === 'issued') return 'success'
    if (status === 'not_redeemed') return 'warning'
    return isShopOrderCancelled(status) ? 'danger' : 'neutral'
}

/** Подсказка владельцу под статусом заказа. */
export function shopOrderOwnerHint(status: ShopOrderStatus): string {
    if (status === 'sent') return 'Клиника подтвердит наличие и подготовит заказ к выдаче.'
    if (status === 'confirmed') return 'Клиника подтвердила заказ. Можно забирать в указанный день.'
    if (status === 'ready') return 'Заказ собран и ждёт вас в клинике.'
    if (status === 'issued') return 'Заказ выдан.'
    if (status === 'not_redeemed') return 'Срок брони истёк, заказ не забрали.'
    return 'Заказ отменён.'
}

/** Подсказка клинике к следующему действию. */
export function shopOrderClinicHint(status: ShopOrderStatus): string {
    if (status === 'sent') return 'Клиент ожидает подтверждения в течение часа в рабочее время.'
    if (status === 'confirmed') return 'Заказ подтверждён. Клиенту ушло СМС.'
    if (status === 'cancelled_by_clinic') return 'Заказ отменён клиникой.'
    if (status === 'cancelled_by_owner') return 'Заказ отменён владельцем.'
    if (status === 'cancelled_by_admin') return 'Заказ отменён поддержкой.'
    if (status === 'expired') return 'Заказ снят по сроку брони.'
    if (status === 'ready') return 'Позиции собраны — отметьте выдачу, когда клиент заберёт заказ.'
    if (status === 'not_redeemed') return 'Срок брони истёк. Если клиент всё-таки пришёл, отметьте выдачу.'
    return ''
}

export const SHOP_ORDER_CLINIC_ACTION_LABELS: Partial<Record<ShopOrderStatus, string>> = {
    confirmed: 'Подтвердить заказ',
    ready: 'Заказ собран',
    issued: 'Отметить выданным',
}

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'})
const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

/** Время показывается в часовом поясе браузера, как и остальное в ЛК. */
export function formatShopOrderDate(iso: string): string {
    return dateFormatter.format(new Date(iso))
}

export function formatShopOrderDateTime(iso: string, compact = false): string {
    const formatted = dateTimeFormatter.format(new Date(iso))

    return compact ? formatted.replace(',', '') : formatted
}

/** Название позиции без формы, если снимок уже заканчивается на «, {форма}». */
export function shopOrderItemTitle(item: {name: string, form?: string | null}): string {
    const form = item.form?.trim()
    const suffix = form ? `, ${form}` : ''

    return suffix && item.name.endsWith(suffix) ? item.name.slice(0, -suffix.length) : item.name
}

/** Строка под названием в карточке владельца: форма и количество. */
export function shopOrderItemMeta(item: {form?: string | null, quantity: number}): string {
    const form = item.form?.trim()

    return form ? `${form} · ×${item.quantity}` : `×${item.quantity}`
}

/** Мета пагинации Laravel → формат компонента Pagination. */
export function shopOrdersPagination(page: ShopOrdersPage | null | undefined): {currentPage: number, lastPage: number} {
    return {currentPage: page?.meta.current_page ?? 1, lastPage: page?.meta.last_page ?? 1}
}
